import React, { useState, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid, 
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  alpha
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import TargetIcon from '@mui/icons-material/GpsFixed';

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  height: "100%",
  padding: theme.spacing(2)
}));

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    opacity: 0,
    transition: 'opacity 0.2s ease'
  },
  '&:hover': {
    boxShadow: '0 6px 16px rgba(15, 40, 76, 0.1)',
    transform: 'translateY(-2px)',
    '&::before': {
      opacity: 1,
    }
  }
}));

interface TargetAudience {
  id: string;
  name: string;
  description: string;
  criteria: string[];
  estimatedReach: number;
  status: 'active' | 'inactive';
  category: 'make-model' | 'vehicle-type' | 'vin' | 'service' | 'equity';
}

interface WorkflowStep {
  id: string;
  step: string;
  description: string;
  completed: boolean;
}

const TargetTab: React.FC = () => {
  const selectedDealer = useSelector(selectDealerSelected);
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  // Sample target audiences data based on design document
  const [targetAudiences] = useState<TargetAudience[]>([
    {
      id: '1',
      name: 'CR-V Owners',
      description: 'Target by make/model - Honda CR-V owners',
      criteria: ['Make: Honda', 'Model: CR-V', 'Active customers'],
      estimatedReach: 1850,
      status: 'active',
      category: 'make-model'
    },
    {
      id: '2',
      name: 'Accord Owners', 
      description: 'Target by make/model - Honda Accord owners',
      criteria: ['Make: Honda', 'Model: Accord', 'Active customers'],
      estimatedReach: 2100,
      status: 'active',
      category: 'make-model'
    },
    {
      id: '3',
      name: 'Compact SUV Buyers',
      description: 'Target by vehicle type - Compact SUV buyer segment',
      criteria: ['Vehicle Type: Compact SUV', 'Purchase intent', 'Age 25-45'],
      estimatedReach: 1650,
      status: 'active',
      category: 'vehicle-type'
    },
    {
      id: '4',
      name: 'Specific VIN Targeting',
      description: 'Target specific vehicles by VIN for recalls or services',
      criteria: ['VIN-based targeting', 'Service required', 'Active recall'],
      estimatedReach: 320,
      status: 'active',
      category: 'vin'
    },
    {
      id: '5',
      name: 'Service Customers',
      description: 'Customers due for maintenance services',
      criteria: ['Service overdue', 'Last service 6+ months', 'Maintenance due'],
      estimatedReach: 890,
      status: 'active',
      category: 'service'
    },
    {
      id: '6',
      name: 'In-Equity Customers',
      description: 'Customers with positive equity in their vehicles',
      criteria: ['Positive equity', 'Vehicle age 2-4 years', 'Trade potential'],
      estimatedReach: 1420,
      status: 'active',
      category: 'equity'
    }
  ]);

  // Workflow steps for Make & Model targeting as per design document
  const [workflowSteps] = useState<WorkflowStep[]>([
    { id: '1', step: 'Step-by-step filtering to define audience', description: 'Set up filters for make, model, year, and other criteria', completed: false },
    { id: '2', step: 'System saves target audience', description: 'Save the filtered audience as a reusable segment', completed: false },
    { id: '3', step: 'Prompts user to pick pre-built templates', description: 'Choose from available email templates or create new', completed: false },
    { id: '4', step: 'If unavailable, starts generation process', description: 'Generate new email content using AI if templates not suitable', completed: false },
    { id: '5', step: 'Confirm and send', description: 'Review campaign details and execute the email send', completed: false }
  ]);

  const handleTargetSelect = useCallback((audience: TargetAudience) => {
    try {
      setLoading(true);
      
      // Simulate API call to select target audience
      setTimeout(() => {
        setSuccessMessage(`Target audience "${audience.name}" selected successfully! Estimated reach: ${audience.estimatedReach} customers.`);
        setLoading(false);
      }, 1500);
      
    } catch (error) {
      console.error('Error selecting target audience:', error);
      setError('Failed to select target audience. Please try again.');
      setLoading(false);
    }
  }, []);

  const handleCreateCustomTarget = useCallback(() => {
    setSuccessMessage('Custom target creation will be available in the next update.');
  }, []);

  const handleAnalyzePerformance = useCallback(() => {
    setSuccessMessage('Performance analysis will be available in the next update.');
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setSuccessMessage('');
  }, []);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <MainContainer>
      <Paper 
        elevation={0}
        sx={{ 
          p: 2.5,
          display: "flex",
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: "space-between",
          alignItems: { xs: 'flex-start', sm: 'center' },
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          gap: 2,
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TargetIcon 
            sx={{ 
              color: theme.palette.primary.main,
              fontSize: 28 
            }} 
          />
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1.1rem',
                color: theme.palette.primary.main
              }}
            >
              Target Page
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: '0.85rem' }}
            >
              Enable Table Marketing Managers to run ad-hoc campaigns targeting specific buyer profiles
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleCreateCustomTarget}
            sx={{ 
              minWidth: 160,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Save Audience Groups
          </Button>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={handleAnalyzePerformance}
            sx={{ 
              minWidth: 160,
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Execute Campaign
          </Button>
        </Box>
      </Paper>

      {/* Key Capabilities */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          mb: 3
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.primary.main,
            mb: 2
          }}
        >
          Key Capabilities
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Audience selection and filtering
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Save audience groups as campaigns
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Use previously generated messages or generate new ones
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '1rem', minWidth: '8px' }}>•</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                Campaign execution
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 600,
          color: theme.palette.primary.main,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        Sample Starting Prompts
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: 2.5,
          padding: 2,
        }}
      >
        {targetAudiences.map((audience) => (
          <StyledCard key={audience.id} onClick={() => handleTargetSelect(audience)}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box 
                sx={{ 
                  p: 2.5,
                  backgroundColor: '#0f284c',
                  color: 'white',
                  borderBottom: '1px solid',
                  borderColor: alpha('#ffffff', 0.1)
                }}
              >
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    mb: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TargetIcon fontSize="small" />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1rem' }}>
                      {audience.name}
                    </Typography>
                  </Box>
                  <Chip 
                    label={audience.status.toUpperCase()} 
                    color={audience.status === 'active' ? 'success' : 'default'}
                    size="small"
                    sx={{
                      height: 20, 
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      borderRadius: '4px'
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.8), fontSize: '0.85rem' }}>
                  {audience.description}
                </Typography>
              </Box>
              
              <Box 
                sx={{ 
                  p: 2.5, 
                  flexGrow: 1,
                  backgroundColor: alpha('#f5f8fa', 0.5),
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      mb: 1
                    }}
                  >
                    <span>Target Criteria:</span>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {audience.criteria.map((criterion, index) => (
                      <Chip 
                        key={index}
                        label={criterion} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                        sx={{ 
                          fontSize: '0.65rem',
                          height: 18,
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                
                <Box sx={{ mt: 'auto' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: theme.palette.primary.main,
                      mb: 1.5,
                      fontSize: '0.85rem'
                    }}
                  >
                    Estimated Reach: {audience.estimatedReach.toLocaleString()} customers
                  </Typography>
                  <Button 
                    variant={audience.status === 'active' ? 'contained' : 'outlined'}
                    color="primary"
                    size="small"
                    fullWidth
                    disabled={loading}
                    sx={{ 
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTargetSelect(audience);
                    }}
                  >
                    {loading ? <CircularProgress size={16} color="inherit" /> : 'Select Target'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </StyledCard>
        ))}
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          mt: 3, 
          p: 3, 
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <TipsAndUpdatesIcon 
            sx={{ 
              color: theme.palette.primary.main,
              fontSize: 24 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.primary.main
            }}
          >
            Workflow Example (Make & Model Target)
          </Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          {workflowSteps.map((step, index) => (
            <Box 
              key={step.id}
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 2, 
                mb: 2,
                p: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              <Box
                sx={{
                  minWidth: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  mt: 0.25
                }}
              >
                {index + 1}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: theme.palette.primary.main }}>
                  {step.step}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
                  {step.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
};

export default TargetTab;
